/*
 * product-addons
 *
 * Adds the ticked add-ons to the cart immediately before the theme's own add-to-cart
 * request, then lets that request run untouched. Two reasons it works this way:
 *
 * 1. The page has two cart paths. <product-form> submits the form over AJAX, and the
 *    quick-buy button in buy-buttons.liquid runs its own inline fetch and redirects.
 *    Both are intercepted here, so add-ons cannot be dropped by using one or the other.
 * 2. Going first means the sections the theme requests already contain the add-ons, so
 *    the mini cart opens once with everything in it rather than flashing an update.
 *
 * Listeners are registered on document in the capture phase. A listener on the form
 * itself would fire in registration order against <product-form>'s own handler, which
 * depends on custom element upgrade order and is not reliable.
 */
if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.errorEl = this.querySelector('[data-error]');
        this.expressNote = this.querySelector('[data-express-note]');
        this.passThrough = false;
        /*
         * Rows marked preselected in Liquid arrive ticked. uncheckAll() must put them
         * back rather than clear them, or a gift meant to ride along with every purchase
         * disappears after the first add and only returns on a reload.
         */
        this.preselected = this.inputs.filter((input) => input.defaultChecked);

        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onTooltipClick.bind(this));
        document.addEventListener('keydown', this.onKeydown);

        if (this.form) {
          document.addEventListener('submit', this.onSubmitCapture, true);
          document.addEventListener('click', this.onClickCapture, true);
        }

        this.syncExpress();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmitCapture, true);
        document.removeEventListener('click', this.onClickCapture, true);
        document.removeEventListener('keydown', this.onKeydown);
      }

      get inputs() {
        return Array.from(this.querySelectorAll('.product-addons__input'));
      }

      get selectedItems() {
        return this.inputs
          .filter((input) => input.checked)
          .map((input) => ({
            id: Number(input.dataset.variantId),
            quantity: Number(input.dataset.quantity || 1),
          }));
      }

      onChange() {
        this.showError('');
        this.syncExpress();
      }

      /*
       * Express payment buttons go straight to checkout with the form's single line item,
       * so an add-on ticked here would be silently lost. Hide the button while anything
       * is selected rather than let that happen.
       */
      syncExpress() {
        if (this.dataset.hideExpress !== 'true' || !this.form) return;
        const express = this.form.querySelector('.shopify-payment-button');
        if (!express) return;
        const active = this.selectedItems.length > 0;
        express.toggleAttribute('hidden', active);
        if (this.expressNote) this.expressNote.toggleAttribute('hidden', !active);
      }

      onTooltipClick(evt) {
        const button = evt.target.closest('.product-addons__why');
        if (!button) {
          this.closeTooltips();
          return;
        }
        const open = button.getAttribute('aria-expanded') === 'true';
        this.closeTooltips();
        button.setAttribute('aria-expanded', open ? 'false' : 'true');
      }

      onKeydown(evt) {
        if (evt.key === 'Escape') this.closeTooltips();
      }

      closeTooltips() {
        this.querySelectorAll('.product-addons__why[aria-expanded="true"]').forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });
      }

      onSubmitCapture(evt) {
        if (evt.target !== this.form || this.passThrough) return;
        const items = this.selectedItems;
        if (!items.length) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();

        this.addItems(items).then(() => {
          this.resubmit(() => {
            if (typeof this.form.requestSubmit === 'function') {
              this.form.requestSubmit();
            } else {
              this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          });
        });
      }

      onClickCapture(evt) {
        if (this.passThrough) return;
        const button = evt.target.closest && evt.target.closest('.button--go-to-cart');
        if (!button || !this.form || !this.form.contains(button)) return;
        const items = this.selectedItems;
        if (!items.length) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();

        this.addItems(items).then(() => {
          this.resubmit(() => button.click());
        });
      }

      resubmit(run) {
        this.passThrough = true;
        try {
          run();
        } finally {
          this.passThrough = false;
        }
      }

      /*
       * On failure the primary add is still allowed through. Blocking the main purchase
       * because an add-on failed would be the worse trade, so the message stays visible
       * and the customer can tick it again.
       */
      addItems(items) {
        const base = (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add';
        const url = base.endsWith('.js') ? base : `${base}.js`;

        return fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: items }),
        })
          .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
          .then(({ ok, data }) => {
            if (!ok) {
              this.showError(data.description || data.message || '');
              return;
            }
            this.uncheckAll();
          })
          .catch((error) => {
            console.error('product-addons:', error);
            this.showError('');
          });
      }

      uncheckAll() {
        this.inputs.forEach((input) => {
          input.checked = this.preselected.includes(input);
        });
        this.syncExpress();
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.toggleAttribute('hidden', !message);
      }
    }
  );
}